import { Spinner } from '@toggle/design-system';
import { formatFileSize } from '@toggle/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { UploadedFile } from '~/views/alfred/research/use-user-upload/use-user-upload.types';
import { useUserUpload } from '~/views/alfred/research/use-user-upload/useUserUpload';
import {
  allowedFileTypes,
  imageTypes,
} from '~/views/alfred/research/utils/formatUploadFiles';

import * as S from './SingleFile.styles';

const imageMimeTypes = Object.values(imageTypes).map(type => type.mimeType);

interface SingleFileProps
  extends Omit<UploadedFile, 'id' | 'fileUrl' | 'fileData'> {
  editable?: boolean;
}

export const SingleFile = ({
  field_name,
  fileType,
  thumbnailUrl,
  name,
  status,
  sizeInBytes,
  errorMessage,
  editable = true,
}: SingleFileProps) => {
  const { t } = useTranslation(['chat']);

  const removeFile = useUserUpload(state => state.removeFile);

  const locales = {
    byte: t('chat:fileSizes.byte'),
    kilobyte: t('chat:fileSizes.kilobyte'),
    megabyte: t('chat:fileSizes.megabyte'),
    gigabyte: t('chat:fileSizes.gigabyte'),
    terabyte: t('chat:fileSizes.terabyte'),
  };

  const imageSrc = (() => {
    const fileTypeObj = Object.values(allowedFileTypes).find(
      config => config.mimeType === fileType
    );

    if (imageMimeTypes.includes(fileType)) {
      return thumbnailUrl ?? fileTypeObj?.icon;
    }

    return fileTypeObj?.icon ?? undefined;
  })();

  return (
    <S.SingleFileRoot $editable={editable}>
      <S.MainContent>
        <S.PreviewWrapper>
          <S.IconWrapper>
            <div>
              {fileType.startsWith('image/') && status !== 'complete' && (
                <S.GreyOverlay data-testid="grey-overlay" />
              )}
              <S.StyledImage
                data-testid="file-icon"
                src={imageSrc}
                $status={status}
                $notImage={!fileType.startsWith('image/')}
                alt={name}
              />
            </div>
          </S.IconWrapper>
          {status === 'uploading' && (
            <S.SpinnerWrapper>
              <Spinner size="small" />
            </S.SpinnerWrapper>
          )}
          {status === 'error' && (
            <S.StyledErrorIcon
              iconName="ExclamationCircle"
              size={16}
              fillColor="var(--icon-critical)"
            />
          )}
        </S.PreviewWrapper>
        <S.FileInfoWrapper>
          <S.FileName $status={status}>{name}</S.FileName>
          <S.FileSize $status={status}>
            {formatFileSize({ sizeInBytes, decimals: 2, locales })}
            {status === 'error' && errorMessage && (
              <S.ErrorMessage data-testid="error-message">
                {errorMessage}
              </S.ErrorMessage>
            )}
          </S.FileSize>
        </S.FileInfoWrapper>
      </S.MainContent>
      {editable && (
        <S.StyledIcon
          iconName="Close"
          onClick={() => {
            removeFile(field_name);
          }}
          size={12}
        />
      )}
    </S.SingleFileRoot>
  );
};
